import React, {useContext, useEffect, useState} from "react";
import {useGoogleReCaptcha} from 'react-google-recaptcha-v3'
import {useForm, SubmitHandler} from "react-hook-form";
import {
    StyledComment,
    StyledCommentContainer,
    StyledCommentContent,
    StyledCommentSection,
    StyledCustomizeOverlay,
    StyledIconTile,
    ErrorMessage
} from './style';

import IconCustomizer from './customizer'

import {CommentContextProvider, useCommentContext, CommentContext} from './context';

import {
    CommentContextProps,
    CommentData,
    CommentProps,
    CommentSectionProps,
    SendCommentData,
} from './interfaces';

import {FaMarkdown, FaMinus, FaPlus, FaWrench, FaReply, FaEdit} from "react-icons/fa";
import {getAnimal, getSecret, getUid, getUserName, setCookie, getColor, makeToken} from "../use-cookie";

import {formatDate, capitalizeFirstLetter, MarkdownToHtml} from "./methods";

import {Animal, Color} from "./animals";

const AWS_GATEWAY_URL = 'https://h29r3fsh33.execute-api.eu-west-1.amazonaws.com/default/maartenpoirot-commentSubmissionHandler'

const api_key = process.env.AWS_COMMENT_API_KEY ?? "";

const admin_uid = process.env.ADMIN_UID ?? "";
export const rootId = "0000000000000000";

const curDate = () => {
    return new Date().toISOString().split("T")[0]
}
const CommentAvatar: React.FC<{
    shouldHide: boolean;
    isParentCollapsed: boolean;
}> = ({ shouldHide, isParentCollapsed }) => {
    const { animal, color, set, editState, context, commentId, authorId, isDeleted} = useCommentContext();
    return (
        <StyledIconTile
            onClick={editState ? set.isCustomizing : () => context.toggleCollapse(commentId)}
            color={color}
            isAdmin={authorId === admin_uid}
            size={shouldHide || isDeleted ? 0.5 : 1}
        >
            {(authorId === admin_uid) ? (
                <img src={`/zoo/favicon.svg`} alt={animal} />
            ) : (
                <img src={`/zoo/${animal}.webp`} alt={animal} />
            )}
            {!isParentCollapsed && (
                <StyledCustomizeOverlay>
                    {editState ? <FaWrench/> : shouldHide ? <FaPlus/> : <FaMinus/>}
                </StyledCustomizeOverlay>
            )}
        </StyledIconTile>
    );
};

type FormData = {
  name: string;
  message: string;
};


const CommentForm: React.FC<{shouldHide: boolean, isReply: boolean, stopReplying: ((state: boolean) => void) | undefined}> = ({
    shouldHide,
    isReply,
    stopReplying
}) => {
    const { executeRecaptcha } = useGoogleReCaptcha();
    const {
        authorName, date, canCancel, isEdited, set, message, isDeleted, isOwned,
        commentId, animal, color, context, editState, isReplying
    } = useCommentContext();

    const [submitted, setSubmitted] = useState<boolean>(false);
    const [submittingText, setSubmittingText] = useState<string>('');
    const [showMd, setShowMd] = useState<boolean>(true);

    const mdToggle = () => {
        setShowMd(!showMd);
    };

    const {
        register,
        handleSubmit,
        formState: {errors, isSubmitting}
    } = useForm();

    const handleDelete = () => {
        set.message("DELETED");
        onSubmit({ name: authorName, message: "DELETED" });
        set.editState(false);
        set.setDeleted();
    };

    const onSubmit: SubmitHandler<FormData> = async ({name, message}) => {
        if (submitted) {
            setSubmitted(false);
            return;
        } else {
            setSubmitted(true);
        }

        if (!executeRecaptcha) {
            return;
        }

        try {
            setSubmittingText("Submitting");

            const recaptchaToken = await executeRecaptcha('comment');
            if (!recaptchaToken) {
                throw new Error("Failed to retrieve reCAPTCHA token.");
            }

            const payload: SendCommentData = {
                animal,
                authorId: getUid(),
                authorName: name.trim() || authorName,
                authorSecret: getSecret(),
                color,
                commentId,
                message: message.trim(),
                parentId: context.parentId,
                recaptcha: recaptchaToken,
                slug: context.slug,
            };

            console.log(payload);

            const response = await fetch(AWS_GATEWAY_URL, {
                method: 'POST',
                mode: 'cors',
                cache: 'no-cache',
                body: JSON.stringify(payload),
                headers: {
                    'Content-Type': 'application/json',
                    'X-Api-Key': api_key,
                },
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`API responded with ${response.status}: ${errorText}`);
            }

            const data = await response.json();
            console.log(`✅ ${response.status}: ${data.message}`);

            setCookie('spirit-animal', animal)
            setCookie('spirit-color', color)
            setCookie('spirit-name', name.trim() || authorName)

            set.editState(false);  // Hide the form and show only markdown preview
        } catch (error) {
            console.error("❌ Error submitting comment:", error);
            setSubmitted(false); // Reset submission state on failure
        } finally {
            setSubmittingText(""); // Clear "Submitting..." message
        }
    };

    return (
        <StyledCommentContent isCollapsed={shouldHide} onSubmit={handleSubmit(onSubmit)} method="post" showMd={showMd}>
            <div className='comment-head'>
                <span style={{width: '100%'}}>
                    {editState ? (
                        <input
                            {...register("name", {
                                minLength: {value: 5, message: "your name should exceed 5 characters"},
                                maxLength: {value: 50, message: "your name cannot exceed 50 characters"},
                            })}
                            type="text"
                            name="name"
                            id="name"
                            placeholder={authorName}
                            disabled={isSubmitting}
                            onChange={(e) => {
                                context.setAuthorName(e.target.value);
                            }}
                        />
                    ) : (
                        <span className='author'>{authorName}</span>
                    )}
                    {!shouldHide && !editState &&
                        <span className='date'>
                            {isDeleted ? "deleted" : isEdited ? "edited" : ""} {formatDate(date)}
                        </span>
                    }
                </span>
                {/*TODO: implement self replying*/}
                <span>
                    {!isDeleted && !shouldHide && canCancel && !isReply && !isReplying && (
                        context.isAdmin ? (
                            <>
                                <FaEdit className='button-like' onClick={set.editToggle}/>
                                <FaReply className='button-like' onClick={set.replyToggle}/>
                            </>
                        ) : (
                        isOwned ?  (
                            <FaEdit className='button-like' onClick={set.editToggle}/>
                        ) : (
                            <FaReply className='button-like' onClick={set.replyToggle}/>
                        ))
                    )}
                </span>
            </div>

            <div className='comment-body'>
                {editState ? (
                    <>
                        <div className="column-container">
                            <textarea
                                {...register("message", {
                                    minLength: {value: 10, message: "Your message must be at least ten characters"},
                                    maxLength: {value: 2000, message: "Your message cannot exceed 2000 characters"},
                                    required: 'A message is required',
                                })}
                                name='message'
                                value={message}
                                placeholder='Share your thoughts...'
                                disabled={isSubmitting}
                                onChange={e => set.message(e.target.value)}
                                style={{ height: `${2 + 1.15 * Math.max(message.split('\n').length, 1)}em`, }}
                            />
                            {showMd && <MarkdownToHtml text={message} />}
                        </div>
                        <FaMarkdown size='26px' className='markdown-icon button-like' onClick={mdToggle}/>
                        <ErrorMessage className={errors.name || errors.message ? "" : "placeholder"}>
                            {errors.message?.message}
                            {errors.name && errors.message && " and "}
                            {errors.name && !errors.message ? capitalizeFirstLetter(errors.name.message) : errors.name?.message}.
                        </ErrorMessage>
                    </>
                ) : (!isDeleted && <MarkdownToHtml text={message} />)}
            </div>
            {/*TODO: implement sending message DELETED*/}
            {editState && (
                <div>
                    {/* For all edit states, new, reply and edit.*/}
                    <button disabled={isSubmitting}>{submittingText || "Submit"}</button>
                    {/* For edit state of editing existing comments*/}
                    {!isReply && canCancel && (<>
                        <button onClick={() => set.editState(false)} disabled={isSubmitting}>Cancel</button>
                        <button onClick={handleDelete} disabled={isSubmitting}>Delete</button>
                    </>)}
                    {/* For edit state of replies*/}
                    {isReply && <button onClick={stopReplying} disabled={isSubmitting}>Cancel</button>}
                </div>
            )}
        </StyledCommentContent>
    );
};


const Comment: React.FC<CommentProps> = (props) => {
    const { commentId, depth, isOwned, context, isReply } = props
    let stopReplying = (_: boolean) => {}
    if ( useContext(CommentContext) !== null ) {
        const { isReplying, set } = useCommentContext();
        stopReplying = set.isReplying;
        if (!isReplying) {
            return null
        }
    }

    const isCollapsed = context.collapsedComments.get(commentId) || false;
    const isParentCollapsed = context.parentId ? context.collapsedComments.get(context.parentId) || false : false;
    const shouldHide = isCollapsed || isParentCollapsed;

    return (
        <>
        <CommentContextProvider
            {...props}>
            <StyledCommentContainer>
                <StyledComment depth={depth ?? 0} hide={false}>
                    <CommentAvatar
                        shouldHide={shouldHide}
                        isParentCollapsed={isParentCollapsed}
                    />
                    <CommentForm
                        shouldHide={shouldHide}
                        isReply={isReply}
                        stopReplying={stopReplying}
                    />
                </StyledComment>
                {isOwned && <IconCustomizer/>}
            </StyledCommentContainer>
            {!context.isAdmin && <Comment
                {...props}
                animal={context.animal}
                authorId={getUid()}
                authorName={context.authorName}
                canCancel={true}
                color={context.color}
                commentId={makeToken(16)}
                context={{ ...context, parentId: commentId }}
                date={curDate()}
                depth={depth + 1}
                isApproved={false}
                isEdited={false}
                isEditable={true}
                isOwned={true}
                isReply={true}
                message=''
            />}
        </CommentContextProvider>
        </>
    );
};


const CommentSection: React.FC<CommentSectionProps> = ({ slug }) => {
    // An array of all comments and whether they are collapsed
    const [comments, setComments] = useState<CommentProps[]>([]);
    const [collapsedComments, setCollapsedComments] = useState<Map<string, boolean>>(new Map());

    const toggleCollapse = (commentId: string) => {
        setCollapsedComments((prev) => {
            const newCollapsed = new Map(prev);
            const isCurrentlyCollapsed = newCollapsed.get(commentId) || false;
            newCollapsed.set(commentId, !isCurrentlyCollapsed);
            return newCollapsed;
        });
    };

    // TODO: Add pagination
    const maxComments = 10;

    // Comment section-wide properties of the visiting user
    const [userAnimal, setUserAnimal] = useState<Animal>(getAnimal());
    const [userColor, setUserColor] = useState<Color>(getColor());
    const [userName, setUserName] = useState<string>(getUserName(userAnimal));

    const contextProps: CommentContextProps = {
        collapsedComments: collapsedComments,
        isAdmin: getUid() === admin_uid,
        parentId: rootId,
        slug: slug,
        toggleCollapse: toggleCollapse,
        animal: userAnimal,
        color: userColor,
        authorName: userName,
        setAnimal: setUserAnimal,
        setColor: setUserColor,
        setAuthorName: setUserName,
    }

    const fetchComment = async (commentId: string, depth: number, collectedComments: Map<string, CommentData>): Promise<void> => {
        if (collectedComments.size >= maxComments) return;

        // Location of the Cloudfront distributed files
        const cloudFrontSource = `https://meta.maartenpoirot.com${slug}comments/`;

        try {
            const response = await fetch(cloudFrontSource + commentId);
            if (!response.ok) throw new Error(`Failed to fetch ${commentId}`);

            const commentJson: CommentData = await response.json();

            const commentData: CommentProps = {
                ...commentJson,
                canCancel: true,
                commentId: commentId,
                context: contextProps,
                depth: depth,
                isEditable: false,
                isDeleted: commentJson.message === 'DELETED',
                isOwned: commentJson.authorId == getUid(),
                isReply: false,
            };

            if (commentId !== rootId) {
                collectedComments.set(commentId, commentData);
            }

            for (const replyId of [...commentJson.replies].reverse()) {
                if (collectedComments.size >= maxComments) return;
                await fetchComment(replyId, depth + 1, collectedComments);
            }
        } catch (error) {
            console.error(`Error fetching comment ${commentId}:`, error);
        }
    };

    useEffect(() => {
        const loadComments = async () => {
            const collectedComments = new Map<string, CommentProps>();
            await fetchComment(rootId, -1, collectedComments);
            setComments(Array.from(collectedComments.values()));
        };

        loadComments().then();
    }, []);

    const commentFallback = {
        // animal is always provided
        authorId: getUid() ?? "",
        // authorName is always provided
        // color is always provided
        date: curDate(),
        isApproved: false,
        isEdited: false,
        message: "",
        replies: [],
        canCancel: false,
        commentId: makeToken(16),
        context: contextProps,
        depth: 0,
        isEditable: true,
        isDeleted: false,
        isOwned: true,
        isReply: false,
    }

    const children: Record<string, string> = {}

    return (
        <StyledCommentSection>
            <Comment {...commentFallback} animal={userAnimal} color={userColor} authorName={userName}/>
            <hr/>
            {comments.map((comment) => {
                if (comment.replies.length > 0) {
                    comment.replies.forEach((reply) => {
                        children[reply] = comment.commentId
                    })
                }
                return (
                    <Comment {...comment} context={{...contextProps, parentId: children[comment.commentId] ?? rootId}}/>
            )})}
        </StyledCommentSection>
    );
};

export default CommentSection;
